/* Websitecontent beheren (/crm/website/site, ADR 0024): wat op greenvision.energy staat.
 *
 *   overzicht   de soorten content, met hoeveel er staan, en de ingang naar de vaste pagina's,
 *               de instellingen, de doorverwijzingen en de media
 *   lijst       per soort: titel, adres op de site, gepubliceerd of concept; bij het team, de
 *               citaten en de logo's (data/lijsten.ts) ook het beeld en het volgnummer. Ook een
 *               teamlid is gepubliceerd of concept: zo haal je iemand van de site
 *   bewerken    de tekstvelden, de slug en publiceren; bij oplossingen, diensten en branches ook
 *               het lijstje, de tekstvakken rechts en de voordelen (Blokbewerkers.tsx); bij
 *               oplossingen en diensten de kaart "In het kort" en bij projectverhalen de plaats
 *               op de projectkaart (Aanvullingen.tsx); bij oplossingen één dag als grafiek
 *               (Dag.tsx) en het schuifbeeld (Schuifbeeld.tsx)
 *   pagina's          staat in Paginas.tsx: de vaste pagina's (home, contact, tools, …)
 *   instellingen      staat in Instellingen.tsx
 *   doorverwijzingen  staat in Doorverwijzingen.tsx
 *   media             staat in Media.tsx; de knop "Kies uit media" bij een afbeeldingsveld in MediaKiezer.tsx
 *
 * Hier wordt niets verwijderd: er is geen knop voor. Wat een publicatievlag heeft, haal je van de
 * site door het terug te zetten naar concept. De enige uitzondering is een doorverwijzing: die kent
 * geen concept en is op haar eigen scherm te verwijderen (Doorverwijzingen.tsx, ADR 0024).
 *
 * Los van de oude planningsmodule op /crm/website (taken, ideeën, notities, tekstconcepten):
 * die raakt de site niet. Na het opslaan vraagt de CRM de site om te verversen (ververs.ts,
 * ADR 0031): een wijziging staat er dan direct, en anders na hooguit 10 minuten. */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, ExternalLink, Globe, Plus, Save } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { useLijst, useNieuw, useOpslaan, useRechten, useRij } from './hooks';
import { SOORTEN, controleer, kentPublicatie, maakSlug, padVan, soortVoor, volgendeVolgorde, type ContentSoort, type Rij, type Veld } from './data/website';
import { KOLOM as DAG } from './data/dag';
import { KOLOM as KERNGEGEVENS } from './data/kerngegevens';
import { KOLOM as SCHUIFBEELD } from './data/schuifbeeld';
import { adresVanPad } from './data/media';
import { InHetKortKaart, PlaatsKaart } from './Aanvullingen';
import { BlokKaart } from './Blokbewerkers';
import { DagKaart } from './Dag';
import { Beeldveld } from './MediaKiezer';
import { SchuifbeeldKaart } from './Schuifbeeld';
import { meldOpslag } from './ververs';

/** Het laatste deel van het adres van de instellingen: /crm/website/site/instellingen. Geen soort
 *  content mag deze sleutel krijgen (tests/website/instellingen-scherm.node.test.mjs kijkt dat na). */
export const INSTELLINGEN_PAD = 'instellingen';
/** Hetzelfde voor de doorverwijzingen: /crm/website/site/doorverwijzingen
 *  (tests/website/doorverwijzingen-scherm.node.test.mjs kijkt het na). */
export const DOORVERWIJZINGEN_PAD = 'doorverwijzingen';
/** Hetzelfde voor de media: /crm/website/site/media (tests/website/media-scherm.node.test.mjs kijkt het na). */
export const MEDIA_PAD = 'media';
/** Hetzelfde voor de vaste pagina's: /crm/website/site/paginas, met het formulier van één pagina op
 *  /crm/website/site/paginas/:id (tests/website/paginas-scherm.node.test.mjs kijkt het na). */
export const PAGINAS_PAD = 'paginas';

const SITE = 'https://greenvision.energy';
const VERVERSING = 'Een wijziging staat direct op de site, en anders na hooguit 10 minuten.';

export function LetOp({ tekst, sleutel }: { tekst: string; sleutel: string }) {
  return (
    <p className="flex max-w-3xl items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-sm text-amber-700" data-let-op={sleutel}>
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> <span>{tekst}</span>
    </p>
  );
}

export function Fout({ tekst }: { tekst: string }) {
  return (
    <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive" role="alert">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <span>{tekst}</span>
    </div>
  );
}

export function Kop({ titel, terug, children }: { titel: string; terug?: { naar: string; label: string }; children?: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        {terug && (
          <Link to={terug.naar} className="mb-1 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-3.5 w-3.5" /> {terug.label}
          </Link>
        )}
        <h1 className="flex items-center gap-2 text-2xl font-bold"><Globe className="h-6 w-6" /> {titel}</h1>
      </div>
      <div className="flex items-center gap-2">{children}</div>
    </div>
  );
}

/* ── Overzicht ─────────────────────────────────────────────────────────────── */

function SoortTegel({ soort }: { soort: ContentSoort }) {
  const { data, error, isLoading } = useLijst(soort);
  const concepten = (data ?? []).filter((r) => kentPublicatie(soort, r) && r.published !== true).length;
  return (
    <Link to={`/crm/website/site/${soort.sleutel}`} className="block">
      <Card className="h-full transition-colors hover:border-primary">
        <CardHeader className="pb-2"><CardTitle className="text-base">{soort.meervoud}</CardTitle></CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          {error ? <span className="text-destructive">Niet te lezen</span> : isLoading ? 'Laden…' : (
            <>
              <span className="text-2xl font-bold text-foreground">{data?.length ?? 0}</span>
              {concepten > 0 && <span className="ml-2">waarvan {concepten} concept{concepten === 1 ? '' : 'en'}</span>}
            </>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}

export function WebsiteOverzicht() {
  return (
    <div className="space-y-6">
      <Kop titel="Website: content op de site" terug={{ naar: '/crm/website', label: 'Website' }}>
        <a href={SITE} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          greenvision.energy <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </Kop>
      <p className="max-w-3xl text-sm text-muted-foreground">
        Dit is wat bezoekers op greenvision.energy zien. {VERVERSING} Taken, ideeën en notities over de site staan onder <Link to="/crm/website/planning" className="underline">planning</Link>.
      </p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {SOORTEN.map((s) => <SoortTegel key={s.sleutel} soort={s} />)}
        <Link to={`/crm/website/site/${PAGINAS_PAD}`} className="block" data-tegel="paginas">
          <Card className="h-full transition-colors hover:border-primary">
            <CardHeader className="pb-2"><CardTitle className="text-base">Pagina&apos;s</CardTitle></CardHeader>
            <CardContent className="text-sm text-muted-foreground">De vaste pagina&apos;s: de foto bovenaan, en bij de homepage en de rekentools ook de kop.</CardContent>
          </Card>
        </Link>
        <Link to={`/crm/website/site/${INSTELLINGEN_PAD}`} className="block" data-tegel="instellingen">
          <Card className="h-full transition-colors hover:border-primary">
            <CardHeader className="pb-2"><CardTitle className="text-base">Instellingen</CardTitle></CardHeader>
            <CardContent className="text-sm text-muted-foreground">Contact, de rekentools en de getallenbalk op de homepage.</CardContent>
          </Card>
        </Link>
        <Link to={`/crm/website/site/${DOORVERWIJZINGEN_PAD}`} className="block" data-tegel="doorverwijzingen">
          <Card className="h-full transition-colors hover:border-primary">
            <CardHeader className="pb-2"><CardTitle className="text-base">Doorverwijzingen</CardTitle></CardHeader>
            <CardContent className="text-sm text-muted-foreground">Oude adressen die doorsturen naar een nieuw adres.</CardContent>
          </Card>
        </Link>
        <Link to={`/crm/website/site/${MEDIA_PAD}`} className="block" data-tegel="media">
          <Card className="h-full transition-colors hover:border-primary">
            <CardHeader className="pb-2"><CardTitle className="text-base">Media</CardTitle></CardHeader>
            <CardContent className="text-sm text-muted-foreground">De beelden en pdf&apos;s van de site: zoeken, uploaden en beschrijven.</CardContent>
          </Card>
        </Link>
      </div>
    </div>
  );
}

/* ── Lijst ─────────────────────────────────────────────────────────────────── */

export function WebsiteLijst() {
  const { soort: sleutel } = useParams();
  const soort = soortVoor(sleutel);
  const { magBewerken } = useRechten();
  const { data, error, isLoading } = useLijst(soort);

  if (!soort) return <Fout tekst={`Onbekende soort content: ${sleutel}.`} />;
  return (
    <div className="space-y-4">
      <Kop titel={soort.meervoud} terug={{ naar: '/crm/website/site', label: 'Content op de site' }}>
        {magBewerken && (
          <Button asChild size="sm"><Link to={`/crm/website/site/${soort.sleutel}/nieuw`}><Plus className="mr-1 h-4 w-4" /> {soort.enkelvoud}</Link></Button>
        )}
      </Kop>
      {soort.uitleg && <p className="max-w-3xl text-sm text-muted-foreground" data-uitleg={soort.sleutel}>{soort.uitleg}</p>}
      {error && <Fout tekst={(error as Error).message} />}
      {isLoading && <p className="text-sm text-muted-foreground">Laden…</p>}
      {data && data.length === 0 && <p className="text-sm text-muted-foreground">Nog niets.</p>}
      {data && data.length > 0 && (
        <Card>
          <CardContent className="divide-y p-0">
            {data.map((rij) => {
              const pad = padVan(soort, rij);
              const onder = soort.ondertitel
                ? soort.ondertitel.map((kolom) => rij[kolom]).filter(Boolean).join(' · ')
                : pad ?? (rij.page_slug ? `pagina: ${rij.page_slug}` : '');
              const beeld = soort.beeld ? String(rij[soort.beeld] ?? '') : '';
              return (
                <Link key={rij.id} to={`/crm/website/site/${soort.sleutel}/${rij.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-muted/50" data-rij={rij.id}>
                  {soort.volgnummer && <span className="w-6 shrink-0 text-right text-xs tabular-nums text-muted-foreground" title="Volgorde">{rij.sort_order ?? ''}</span>}
                  {soort.beeld && (beeld.startsWith('/media/')
                    ? <img src={adresVanPad(beeld)} alt="" className={`h-10 w-10 shrink-0 rounded border bg-white ${soort.beeld === 'logo' ? 'object-contain' : 'object-cover'}`} />
                    : <span className="h-10 w-10 shrink-0 rounded border bg-muted" aria-hidden />)}
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{rij[soort.titelKolom] || '(zonder titel)'}</div>
                    <div className="truncate text-xs text-muted-foreground">{onder}</div>
                  </div>
                  {kentPublicatie(soort, rij) && (
                    <Badge variant={rij.published === true ? 'default' : 'secondary'}>{rij.published === true ? 'Gepubliceerd' : 'Concept'}</Badge>
                  )}
                </Link>
              );
            })}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* ── Bewerken ──────────────────────────────────────────────────────────────── */

function VeldInvoer({ veld, waarde, fout, zet, uit }: { veld: Veld; waarde: any; fout?: string; zet: (w: any) => void; uit: boolean }) {
  const id = `veld-${veld.kolom}`;
  const tekst = waarde ?? '';
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{veld.label}{veld.verplicht ? ' *' : ''}</Label>
      {veld.soort === 'lang' && <Textarea id={id} rows={3} value={tekst} disabled={uit} onChange={(e) => zet(e.target.value)} />}
      {veld.soort === 'html' && (
        <div className="grid gap-3 lg:grid-cols-2">
          <div>
            <Textarea id={id} rows={16} value={tekst} disabled={uit} onChange={(e) => zet(e.target.value)} className="font-mono text-xs" />
            <p className="mt-1 text-xs text-muted-foreground">HTML. Rechts zie je hoe het leest; de opmaak van de site zelf kan iets afwijken.</p>
          </div>
          {/* sandbox zonder waarden: geen scripts, geen formulieren, geen navigatie. */}
          <iframe title={`Voorbeeld ${veld.label}`} sandbox="" srcDoc={`<style>body{font-family:system-ui,sans-serif;font-size:14px;line-height:1.6;color:#1a1a1a;margin:12px}img{max-width:100%}</style>${tekst}`} className="h-full min-h-[320px] w-full rounded-md border bg-white" />
        </div>
      )}
      {(veld.soort === 'tekst' || veld.soort === 'getal') && (
        <Input id={id} type={veld.soort === 'getal' ? 'number' : 'text'} value={tekst} disabled={uit} onChange={(e) => zet(e.target.value)} />
      )}
      {/* Kiezen uit de media vult het pad in; zelf typen blijft kunnen (MediaKiezer.tsx). */}
      {veld.soort === 'afbeelding' && <Beeldveld id={id} waarde={String(tekst)} uit={uit} zet={zet} />}
      {veld.uitleg && <p className="text-xs text-muted-foreground">{veld.uitleg}</p>}
      {fout && <p className="text-xs text-destructive">{fout}</p>}
    </div>
  );
}

export function WebsiteBewerk() {
  const { soort: sleutel, id } = useParams();
  const soort = soortVoor(sleutel);
  const nieuw = id === 'nieuw';
  const navigate = useNavigate();
  const { toast } = useToast();
  const { magBewerken } = useRechten();
  const { data: rij, error, isLoading } = useRij(soort, nieuw ? undefined : id);
  /* Iets nieuws in een lijst met een volgnummer komt achteraan: daarvoor is de lijst nodig. Is die
   * niet te lezen, dan begint het vak leeg; het opslaan zegt dan zelf wat er mis is. */
  const achteraan = nieuw && !!soort?.volgnummer;
  const { data: lijst, isLoading: lijstLaadt } = useLijst(achteraan ? soort : null);

  if (!soort) return <Fout tekst={`Onbekende soort content: ${sleutel}.`} />;
  if ((!nieuw && isLoading) || (achteraan && lijstLaadt)) return <p className="text-sm text-muted-foreground">Laden…</p>;
  if (error) return <Fout tekst={(error as Error).message} />;
  if (!nieuw && !rij) return <Fout tekst={`${soort.enkelvoud} niet gevonden.`} />;
  const begin = { ...soort.begin, ...(achteraan && lijst ? { sort_order: volgendeVolgorde(lijst) } : {}) };
  return <Formulier key={nieuw ? 'nieuw' : rij!.id} soort={soort} vorige={nieuw ? null : rij!} begin={begin} magBewerken={magBewerken} klaar={(nieuweId) => navigate(`/crm/website/site/${soort.sleutel}/${nieuweId}`)} toast={toast} />;
}

function Formulier({ soort, vorige, begin, magBewerken, klaar, toast }: { soort: ContentSoort; vorige: Rij | null; begin: Rij; magBewerken: boolean; klaar: (id: string) => void; toast: ReturnType<typeof useToast>['toast'] }) {
  const [concept, setConcept] = useState<Rij>(() => vorige ?? { published: false, slug: '', ...begin });
  const [slugVast, setSlugVast] = useState(!!vorige);
  const [waarschuwing, setWaarschuwing] = useState<string | null>(null);
  const opslaan = useOpslaan(soort);
  const maken = useNieuw(soort);
  const bezig = opslaan.isPending || maken.isPending;
  const uit = !magBewerken || bezig;

  /* Alleen overnemen als de server een nieuwere versie heeft (na opslaan). Een verversing op de
   * achtergrond met dezelfde inhoud mag niet weggooien wat iemand net heeft getypt. */
  const versie = vorige ? `${vorige.id}:${vorige.updated_at ?? ''}` : null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (vorige) setConcept(vorige); }, [versie]);

  const controle = useMemo(() => controleer(soort, concept, vorige), [soort, concept, vorige]);
  const gewijzigd = useMemo(() => !vorige || JSON.stringify(vorige) !== JSON.stringify(concept), [vorige, concept]);
  const slugAnders = !!vorige && soort.heeftSlug && concept.slug !== vorige.slug;
  const pad = padVan(soort, concept);

  const zet = (kolom: string, waarde: any) => setConcept((c) => {
    const volgend = { ...c, [kolom]: waarde };
    // Bij iets nieuws volgt de slug de titel, tot iemand hem zelf aanpast.
    if (!slugVast && soort.heeftSlug && kolom === soort.titelKolom) volgend.slug = maakSlug(String(waarde));
    return volgend;
  });

  const bewaar = async () => {
    setWaarschuwing(null);
    try {
      if (!vorige) {
        const gemaakt = await maken.mutateAsync(concept);
        // Iets zonder publicatie (een veelgestelde vraag) staat meteen op de site; de rest begint als concept, ook een teamlid.
        if (gemaakt.verversing) void meldOpslag(toast, gemaakt.verversing, `${soort.enkelvoud} aangemaakt`);
        else toast({ title: `${soort.enkelvoud} aangemaakt als concept`, description: 'Het concept staat pas op de site nadat je het publiceert.' });
        klaar(gemaakt.rij.id);
        return;
      }
      const uitkomst = await opslaan.mutateAsync({ vorige, concept });
      if (uitkomst.waarschuwing) setWaarschuwing(uitkomst.waarschuwing);
      // De melding staat er meteen; of de site al ververst is, komt er even later bij.
      void meldOpslag(toast, uitkomst.verversing, 'Opgeslagen', uitkomst.redirect ? `Het oude adres ${uitkomst.redirect.van} verwijst door.` : '');
    } catch (fout) {
      toast({ title: 'Niet opgeslagen', description: fout instanceof Error ? fout.message : String(fout), variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-4">
      <Kop titel={vorige ? String(vorige[soort.titelKolom] ?? soort.enkelvoud) : `Nieuw: ${soort.enkelvoud.toLowerCase()}`} terug={{ naar: `/crm/website/site/${soort.sleutel}`, label: soort.meervoud }}>
        {pad && vorige?.published === true && (
          <a href={`${SITE}${vorige.slug ? `${soort.padPrefix}${vorige.slug}` : ''}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            Bekijk op de site <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
        {magBewerken && (
          <Button onClick={bewaar} disabled={bezig || !gewijzigd || !controle.ok} size="sm">
            <Save className="mr-1 h-4 w-4" /> {bezig ? 'Opslaan…' : 'Opslaan'}
          </Button>
        )}
      </Kop>

      {soort.uitleg && <p className="max-w-3xl text-sm text-muted-foreground" data-uitleg={soort.sleutel}>{soort.uitleg}</p>}
      {soort.letOp && <LetOp tekst={soort.letOp} sleutel={soort.sleutel} />}
      {/* De kolom voor de publicatie is er pas na een script (het team): tot dan staat de rij op de site. */}
      {soort.publicatieScript && vorige && !kentPublicatie(soort, vorige) && (
        <LetOp sleutel="publicatie-ontbreekt" tekst={`Terugzetten naar concept kan hier nog niet. De eigenaar van de database draait eerst het script ${soort.publicatieScript} uit de repo van de site (greenvision-site). Tot die tijd staat dit op de site, en elke wijziging ook.`} />
      )}
      {!magBewerken && <Fout tekst="Je kunt deze content lezen, maar niet wijzigen." />}
      {waarschuwing && <Fout tekst={waarschuwing} />}

      <Card>
        <CardContent className="space-y-5 pt-6">
          {kentPublicatie(soort, vorige) && (
            <div className="flex items-center gap-3">
              <Switch id="gepubliceerd" checked={concept.published === true} disabled={uit} onCheckedChange={(aan) => zet('published', aan)} />
              <Label htmlFor="gepubliceerd">{concept.published === true ? 'Gepubliceerd: zichtbaar op de site' : 'Concept: niet zichtbaar op de site'}</Label>
            </div>
          )}

          {soort.heeftSlug && (
            <div className="space-y-1.5">
              <Label htmlFor="veld-slug">Slug (het laatste deel van het adres) *</Label>
              <Input id="veld-slug" value={concept.slug ?? ''} disabled={uit} onChange={(e) => { setSlugVast(true); zet('slug', e.target.value); }} />
              <p className="text-xs text-muted-foreground">{pad ? `${SITE}${pad}` : 'Vul een titel in.'}</p>
              {slugAnders && controle.ok && (
                <p className="text-xs text-amber-600">Het adres verandert. Het oude adres {soort.padPrefix}{vorige!.slug} gaat automatisch doorverwijzen naar het nieuwe.</p>
              )}
              {controle.fouten.slug && <p className="text-xs text-destructive">{controle.fouten.slug}</p>}
            </div>
          )}

          {soort.velden.map((veld) => (
            <VeldInvoer key={veld.kolom} veld={veld} waarde={concept[veld.kolom]} fout={controle.fouten[veld.kolom]} uit={uit} zet={(w) => zet(veld.kolom, w)} />
          ))}
        </CardContent>
      </Card>

      {/* Kolommen die er pas zijn als de eigenaar van de database een script heeft gedraaid. Heeft de
          rij de kolom niet, dan staat dat er en wordt er niets van geschreven (Aanvullingen.tsx). */}
      {soort.kerngegevens && (
        <InHetKortKaart vorige={vorige} waarde={concept[KERNGEGEVENS]} fout={controle.fouten[KERNGEGEVENS]} uit={uit} zet={(w) => zet(KERNGEGEVENS, w)} />
      )}
      {soort.plaats && <PlaatsKaart vorige={vorige} concept={concept} fouten={controle.fouten} uit={uit} zet={zet} />}

      {/* Een blok dat niemand aanraakt, wordt bij opslaan niet geschreven (data/blokken.ts). */}
      {soort.blokken.map((blok) => (
        <BlokKaart key={blok.kolom} blok={blok} opgeslagen={vorige ? vorige[blok.kolom] : undefined} waarde={concept[blok.kolom]} fout={controle.fouten[blok.kolom]} uit={uit} zet={(w) => zet(blok.kolom, w)} />
      ))}

      {/* Wat op de pagina van een oplossing na de tekst komt, staat ook hier na de blokken: eerst de
          dag als grafiek, dan het schuifbeeld. */}
      {soort.dag && <DagKaart vorige={vorige} waarde={concept[DAG]} fout={controle.fouten[DAG]} uit={uit} zet={(w) => zet(DAG, w)} />}
      {soort.schuifbeeld && (
        <SchuifbeeldKaart vorige={vorige} waarde={concept[SCHUIFBEELD]} fout={controle.fouten[SCHUIFBEELD]} uit={uit} zet={(w) => zet(SCHUIFBEELD, w)} />
      )}

      {/* Het formulier is met de blokken lang: onderaan staat de knop er nog een keer. */}
      {magBewerken && (soort.blokken.length > 0 || soort.plaats) && (
        <div className="flex flex-wrap items-center justify-end gap-3">
          {!controle.ok && <p className="text-xs text-destructive">{Object.values(controle.fouten)[0]}</p>}
          <Button onClick={bewaar} disabled={bezig || !gewijzigd || !controle.ok} size="sm">
            <Save className="mr-1 h-4 w-4" /> {bezig ? 'Opslaan…' : 'Opslaan'}
          </Button>
        </div>
      )}

      {vorige && soort.alleenLezen.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Alleen lezen</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">Dit is hier nog niet te bewerken. Het blijft bij opslaan ongewijzigd.</p>
            {soort.alleenLezen.map((kolom) => (
              <details key={kolom} className="rounded-md border p-3 text-sm">
                <summary className="cursor-pointer font-medium">{kolom}</summary>
                <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap text-xs text-muted-foreground">{JSON.stringify(vorige[kolom] ?? null, null, 2)}</pre>
              </details>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
